<template>
	<div class="audio-player-wrapper">
		<div class="audio-player-cover" :style="coverStyle"></div>
		<div class="audio-player-track">
			<audio ref="audioPlayer" class="video-js vjs-default-skin"></audio>
			<button
				ref="speedButton"
				type="button"
				class="audio-player-speed-btn"
				@click="togglePlaybackRate"
			>
				{{ playbackRateLabel }}
			</button>
		</div>
	</div>
</template>

<script>
import videojs from "video.js";
import axios from "axios";
import "video.js/dist/video-js.min.css";
import "videojs-seek-buttons/dist/videojs-seek-buttons.css";
import "videojs-seek-buttons";
import defaultAudioCover from "../../../static/images/audio/default-cover.png";

export default {
	name: "AudioPlayer",
	props: {
		playerData: {
			type: Object,
			required: true,
		},
	},

	data() {
		return {
			player: null,
			last_saved_position: null,
			sent_watch: null,
			current_audio_num: 1,
			posterUrl: null,
			playbackRates: [0.5, 0.75, 1, 1.25, 1.5, 2],
			currentPlaybackRate: 1,
		};
	},

	computed: {
		coverStyle() {
			const coverUrl = this.posterUrl || defaultAudioCover;
			return {
				backgroundImage: `url(${coverUrl})`,
				backgroundSize: "cover",
				backgroundPosition: "center",
			};
		},
		playbackRateLabel() {
			const roundedRate = Number.isInteger(this.currentPlaybackRate) ? this.currentPlaybackRate : this.currentPlaybackRate.toFixed(2).replace(/\.?0+$/, "");
			return `${roundedRate}x`;
		},
	},

	mounted() {
		this.initPlayer();
		this.hideAudioStubOverlay();
		window.audioPlayer = this.player;
	},

	beforeDestroy() {
		if (this.player) {
			this.player.off("ratechange", this.onPlaybackRateChange);
			this.player.dispose();
		}
		if (window.audioPlayer === this.player) {
			window.audioPlayer = null;
		}
	},

	methods: {
		initPlayer() {
			// Получаем текущее аудио
			const currentAudio = this.playerData.audios[this.current_audio_num - 1];
			if (!currentAudio || !currentAudio.sources || currentAudio.sources.length === 0) {
				console.error("No audio sources available");
				return;
			}

			// Устанавливаем poster URL если есть
			if (currentAudio.poster) {
				this.posterUrl = currentAudio.poster;
			}

			// Подготавливаем источники для VideoJS
			const sources = currentAudio.sources.map(s => ({
				src: s.src,
				type: s.type || "audio/mpeg"
			}));

			// Инициализируем VideoJS для аудио + плагин перемотки ±15 сек (videojs-seek-buttons)
			this.player = videojs(this.$refs.audioPlayer, {
				playbackRates: this.playbackRates,
				controls: true,
				preload: "auto",
				fluid: false,
				responsive: false,
				bigPlayButton: false,
				sources: sources,
				controlBar: {
					volumePanel: false,
					fullscreenToggle: false,
					pictureInPictureToggle: false,
					playbackRateMenuButton: false,
				}
			});

			this.player.seekButtons({ forward: 15, back: 15 });

			// Отключаем полноэкранный режим для аудио
			this.player.ready(() => {
				this.currentPlaybackRate = this.player.playbackRate() || 1;
				this.moveSpeedButtonToControlBar();
				this.hideAudioStubOverlay();

				const fullscreenButton = this.player.controlBar.getChild('fullscreenToggle');
				if (fullscreenButton) {
					fullscreenButton.hide();
				}
			});

			this.player.on('ready', this.onPlayerReady);
			this.player.on('timeupdate', this.onPlayerTimeUpdate);
			this.player.on('ended', this.onPlayerEnded);
			this.player.on('play', this.onPlayerPlay);
			this.player.on('error', this.onPlayerError);
			this.player.on("ratechange", this.onPlaybackRateChange);
		},

		onPlayerReady() {
			if (this.playerData.start_position > 0) {
				this.player.currentTime(this.playerData.start_position);
			}
			this.last_saved_position = this.playerData.start_position;
		},

		onPlayerTimeUpdate() {
			if (!this.player) return;

			const position = this.player.currentTime();
			const duration = this.player.duration();
			const end_pos = this.playerData.end_pos || (duration * 0.9);
			const rounded_position = Math.floor(position / 10) * 10;

			// Сохраняем позицию каждые 10 секунд
			if (
				rounded_position > 0 &&
				position < end_pos &&
				this.last_saved_position !== rounded_position
			) {
				this.last_saved_position = rounded_position;
				this.savePosition(rounded_position);
			}

			// Отмечаем как прослушанное при достижении 90%
			if (position > end_pos && !this.sent_watch) {
				this.markAsWatched();
			}
		},

		onPlayerEnded() {
			// Переходим к следующему аудио, если есть
			if (this.current_audio_num < this.playerData.audios.length) {
				this.current_audio_num++;
				this.loadAudio(this.current_audio_num - 1);
			} else {
				// Все аудио прослушаны
				this.markAsWatched();
			}
		},

		onPlayerPlay() {
			// Скрываем заглушку при начале воспроизведения
			const stub = document.querySelector('.j-hide-audio-stub');
			if (stub && !stub.classList.contains('not-showing')) {
				stub.classList.add('not-showing');
				setTimeout(() => {
					stub.classList.add("hide");
				}, 600);
			}
		},

		onPlayerError() {
			console.error('Audio player error:', this.player.error());
		},

		loadAudio(index) {
			const audio = this.playerData.audios[index];
			if (!audio || !audio.sources || audio.sources.length === 0) return;

			// Обновляем poster если есть
			if (audio.poster) {
				this.posterUrl = audio.poster;
			}

			const sources = audio.sources.map(s => ({
				src: s.src,
				type: s.type || "audio/mpeg"
			}));

			this.player.src(sources);
			this.player.load();
		},

		togglePlaybackRate() {
			if (!this.player) return;

			const currentRate = this.player.playbackRate() || 1;
			const currentRateIndex = this.playbackRates.indexOf(currentRate);
			const nextRateIndex = currentRateIndex === -1 || currentRateIndex === this.playbackRates.length - 1 ? 0 : currentRateIndex + 1;
			this.player.playbackRate(this.playbackRates[nextRateIndex]);
		},

		onPlaybackRateChange() {
			if (!this.player) return;

			this.currentPlaybackRate = this.player.playbackRate() || 1;
		},

		moveSpeedButtonToControlBar() {
			if (!this.player || !this.$refs.speedButton || !this.player.controlBar) return;

			const controlBarEl = this.player.controlBar.el();
			if (!controlBarEl || controlBarEl.contains(this.$refs.speedButton)) return;

			controlBarEl.appendChild(this.$refs.speedButton);
		},

		hideAudioStubOverlay() {
			const stub = document.querySelector(".j-hide-audio-stub");
			if (!stub) return;

			stub.classList.add("not-showing");
			stub.classList.add("hide");
		},

		savePosition(position) {
			axios({
				response_type: "json",
				headers: {
					"X-Requested-With": "XMLHttpRequest",
					"X-CSRF-Token": document
						.querySelector('meta[name="csrf-token"]')
						.getAttribute("content"),
				},
				url: this.playerData.save_audio_position_path,
				method: "POST",
				data: {
					saved_audio_position: position,
				},
			})
				.then((response) => {
					this.playerData.start_position = position;
				})
				.catch((errors) => {
					console.warn("Failed to save audio position:", errors);
				});
		},

		markAsWatched() {
			if (this.sent_watch) return;

			axios({
				response_type: "json",
				headers: {
					"X-Requested-With": "XMLHttpRequest",
					"X-CSRF-Token": document
						.querySelector('meta[name="csrf-token"]')
						.getAttribute("content"),
				},
				url: this.playerData.listen_audio_path,
				method: "POST",
			})
				.then((response) => {
					this.sent_watch = true;
					this.toggleLectureWatchedUI();
				})
				.catch((errors) => {
					console.warn("Failed to mark audio as watched:", errors);
				});
		},

		play() {
			if (this.player) {
				this.player.play();
			}
		},

		pause() {
			if (this.player) {
				this.player.pause();
			}
		},

		toggleLectureWatchedUI() {
			document.querySelectorAll(".j-test-enabled").forEach((el) => el.classList.remove("hide"));
			document.querySelectorAll(".j-test-disabled").forEach((el) => el.classList.add("hide"));
			document.querySelectorAll(".j-lecture-finish-btn-holder .message-section__btn.disabled-btn").forEach((btn) => {
				btn.classList.remove("disabled-btn");
				btn.classList.add("action-btn");
			});
		},
	},
};
</script>

<style scoped>
.audio-player-wrapper {
	display: flex;
	align-items: center;
	width: 100%;
	gap: 12px;
	padding: 12px 16px;
	border-radius: 8px;
	background-color: #f5f5f5;
	min-height: 174px;
}

.audio-player-cover {
	flex-shrink: 0;
	width: 150px;
	height: 150px;
	border-radius: 14px;
	background-color: #d1d5db;
	background-repeat: no-repeat;
	background-size: cover;
	background-position: center;
}

.audio-player-track {
	display: flex;
	align-items: center;
	flex: 1 1 auto;
	min-width: 0;
	gap: 10px;
}

.audio-player-speed-btn {
	flex: 0 0 auto;
	order: 7;
	border: 0;
	background: transparent;
	font-size: 14px;
	font-weight: 500;
	line-height: 1;
	color: #333;
	padding: 0 8px;
	cursor: pointer;
}

.audio-player-track .video-js {
	flex: 1 1 auto;
	width: 100%;
	height: 56px;
	min-width: 0;
	background: transparent;
}

.audio-player-track :deep(.vjs-control-bar) {
	display: flex;
	align-items: center;
	height: 56px;
	width: 100%;
	padding: 0;
	background-color: transparent;
}

.audio-player-track :deep(.vjs-big-play-button),
.audio-player-track :deep(.vjs-fullscreen-control),
.audio-player-track :deep(.vjs-picture-in-picture-control),
.audio-player-track :deep(.vjs-volume-panel),
.audio-player-track :deep(.vjs-live-control) {
	display: none !important;
}

.audio-player-track :deep(.vjs-seek-button) {
	display: flex;
	align-items: center;
	justify-content: center;
	width: 34px;
	height: 34px;
	padding: 0;
	box-sizing: border-box;
	border-radius: 50%;
	margin: 0 4px;
	background-color: rgba(17, 24, 39, 0.08);
	color: #333;
	line-height: 34px;
	cursor: pointer;
}

.audio-player-track :deep(.vjs-seek-button .vjs-icon-placeholder) {
	position: relative;
	display: flex;
	align-items: center;
	justify-content: center;
	width: 100%;
	height: 100%;
	pointer-events: auto;
}

.audio-player-track :deep(.vjs-seek-button .vjs-icon-placeholder::before) {
	position: absolute;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
	display: block;
	line-height: 1;
	pointer-events: auto;
}

.audio-player-track :deep(.vjs-seek-button.skip-back .vjs-icon-placeholder::before) {
	top: 15%;
	left: 16%;
}

.audio-player-track :deep(.vjs-seek-button.skip-forward .vjs-icon-placeholder::before) {
	top: 15%;
	left: -15%;
}

.audio-player-track :deep(.vjs-seek-button *) {
	pointer-events: auto;
}

.audio-player-track :deep(.vjs-play-control) {
	display: flex;
	align-items: center;
	justify-content: center;
	width: 44px;
	height: 44px;
	padding: 0;
	box-sizing: border-box;
	border-radius: 50%;
	margin: 0 8px;
	background-color: #2563eb;
	color: #fff;
	cursor: pointer;
}

.audio-player-track :deep(.vjs-seek-button.skip-back) {
	order: 1;
}

.audio-player-track :deep(.vjs-play-control) {
	order: 2;
}

.audio-player-track :deep(.vjs-seek-button.skip-forward) {
	order: 3;
}

.audio-player-track :deep(.vjs-play-control .vjs-icon-placeholder) {
	position: relative;
	display: flex;
	align-items: center;
	justify-content: center;
	width: 100%;
	height: 100%;
	pointer-events: none;
}

.audio-player-track :deep(.vjs-play-control .vjs-icon-placeholder::before) {
	position: absolute;
	top: 77%;
	left: 50%;
	transform: translate(-50%, -50%);
	display: block;
	line-height: 1;
	pointer-events: none;
}

.audio-player-track :deep(.vjs-play-control *) {
	pointer-events: none;
}

.audio-player-track :deep(.vjs-play-control:hover),
.audio-player-track :deep(.vjs-play-control:focus) {
	background-color: #1d4ed8;
}

.audio-player-track :deep(.vjs-current-time),
.audio-player-track :deep(.vjs-duration) {
	display: flex;
	align-items: center;
	padding: 0;
	margin: 0 8px;
	font-size: 14px;
	color: #374151;
}

.audio-player-track :deep(.vjs-current-time),
.audio-player-track :deep(.vjs-current-time-display) {
	order: 4;
}

.audio-player-track :deep(.vjs-time-divider) {
	display: none;
}

.audio-player-track :deep(.vjs-progress-control) {
	flex: 1 1 auto;
	min-width: 80px;
	margin: 0 8px;
	order: 5;
}

.audio-player-track :deep(.vjs-duration),
.audio-player-track :deep(.vjs-duration-display) {
	order: 6;
}

.audio-player-track :deep(.vjs-remaining-time),
.audio-player-track :deep(.vjs-remaining-time-display) {
	display: none !important;
}

.audio-player-track :deep(.vjs-progress-holder) {
	height: 6px;
	border-radius: 999px;
	background-color: #4b5563;
}

.audio-player-track :deep(.vjs-mouse-display),
.audio-player-track :deep(.vjs-time-tooltip) {
	display: none !important;
}

.audio-player-track :deep(.vjs-load-progress) {
	background-color: rgba(255, 255, 255, 0.35);
}

.audio-player-track :deep(.vjs-play-progress) {
	background-color: #2563eb;
}

.audio-player-track :deep(.vjs-play-progress::before) {
	top: -0.28em;
	font-size: 0.85em;
	color: #fff;
}

.audio-player-track :deep(.vjs-playback-rate) {
	display: none !important;
}

@media (max-width: 767px) {
	.audio-player-wrapper {
		flex-direction: column;
		align-items: stretch;
		padding: 8px 12px;
		gap: 10px;
		min-height: auto;
	}

	.audio-player-cover {
		width: 150px;
		height: 150px;
		border-radius: 12px;
		margin: 0 auto;
	}

	.audio-player-track {
		position: relative;
		width: 100%;
		display: block;
	}

	.audio-player-speed-btn {
		position: static;
		flex: 0 0 auto;
		order: 6;
		margin-left: 8px;
		padding: 4px 8px;
	}

	.audio-player-track .video-js {
		flex: 1 1 auto;
		height: auto;
		min-width: 0;
		min-height: 0;
	}

	.audio-player-track :deep(.vjs-control-bar) {
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		align-content: flex-start;
		position: relative;
		height: auto;
		min-height: 0;
		row-gap: 10px;
		column-gap: 0;
		width: 100%;
		overflow: visible;
		padding-top: 2px;
		padding-bottom: 0;
	}

	.audio-player-track :deep(.vjs-progress-control) {
		order: 1;
		flex: 0 0 100% !important;
		width: 100%;
		margin: 0 0 8px;
		max-width: 100%;
		padding: 0;
	}

	.audio-player-track :deep(.vjs-current-time),
	.audio-player-track :deep(.vjs-current-time-display) {
		order: 2;
		margin: 0;
	}

	.audio-player-track :deep(.vjs-duration),
	.audio-player-track :deep(.vjs-duration-display) {
		order: 7;
		margin: 0 0 0 auto;
	}

	.audio-player-track :deep(.vjs-seek-button.skip-back) {
		order: 3;
		margin-left: auto;
	}

	.audio-player-track :deep(.vjs-play-control) {
		order: 4;
	}

	.audio-player-track :deep(.vjs-seek-button.skip-forward) {
		order: 5;
		margin-right: 0;
	}

	.audio-player-track :deep(.vjs-current-time),
	.audio-player-track :deep(.vjs-current-time-display),
	.audio-player-track :deep(.vjs-duration),
	.audio-player-track :deep(.vjs-duration-display) {
		font-size: 13px;
	}

	.audio-player-track :deep(.vjs-play-control) {
		width: 46px;
		height: 46px;
	}

	.audio-player-track :deep(.vjs-seek-button) {
		width: 38px;
		height: 38px;
		line-height: 38px;
	}
}
</style>
